"use client";
import React, { useEffect, useId, useRef } from "react";
import { ShieldCheck } from "lucide-react";
import { SHARED_POLICIES } from "@/lib/privacy/sharedPolicies";
import { consentCopy } from "@/lib/privacy/presentation";

function PolicyLinks({ copy }) {
  return <nav className="consent-links" aria-label={copy.title}>
    <a href={SHARED_POLICIES.privacy} target="_blank" rel="noopener noreferrer">{copy.privacy}</a>
    <a href={SHARED_POLICIES.cookies} target="_blank" rel="noopener noreferrer">{copy.cookies}</a>
  </nav>;
}

export default function ConsentInterface({ publicationName, presentation = {}, config, known, open, draft, setDraft, onOpen, onClose, onAccept, onReject, onSave, error }) {
  const copy = consentCopy(presentation.locale);
  const heading = useId();
  const dialog = useRef(null);
  useEffect(() => {
    if (!open || !dialog.current) return;
    const previous = document.activeElement;
    const element = dialog.current;
    const interfaceRoot = element.closest(".surgedome-consent");
    const background = [];
    for (let branch = element.closest(".consent-overlay"); branch?.parentElement && branch !== document.body; branch = branch.parentElement) {
      for (const sibling of branch.parentElement.children) {
        if (sibling !== branch) { background.push([sibling, sibling.inert]); sibling.inert = true; }
      }
    }
    const focusable = () => [...element.querySelectorAll('button:not(:disabled), input:not(:disabled), a[href]')];
    focusable()[0]?.focus();
    const keyboard = event => {
      if (event.key === "Escape") { event.preventDefault(); onClose(); }
      if (event.key !== "Tab") return;
      const items = focusable(), first = items[0], last = items.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    element.addEventListener("keydown", keyboard);
    return () => {
      element.removeEventListener("keydown", keyboard);
      for (const [node, inert] of background) node.inert = inert;
      if (previous?.isConnected && previous !== document.body) previous.focus();
      else interfaceRoot?.querySelector(".consent-revisit, .consent-panel button")?.focus();
    };
  }, [open, onClose]);
  const actions = <div className="consent-actions">
    <button type="button" className="consent-accept" onClick={onAccept}>{copy.accept}</button>
    <button type="button" onClick={onReject}>{copy.reject}</button>
    {!open && <button type="button" onClick={onOpen}>{copy.manage}</button>}
  </div>;
  return <div className="surgedome-consent" lang={presentation.locale || "en"} style={{ fontFamily: presentation.fontFamily || "system-ui, sans-serif" }}>
    {!open && !known && <section className="consent-panel" aria-labelledby={heading}>
      <h2 id={heading}><ShieldCheck size={26} aria-hidden="true" />{copy.heading}</h2>
      <p className="consent-body">{copy.body(publicationName)}</p>
      {actions}<PolicyLinks copy={copy} />
      {error && <p role="alert">{copy.error}</p>}
    </section>}
    {open && <div className="consent-overlay">
      <section ref={dialog} className="consent-dialog" role="dialog" aria-modal="true" aria-labelledby={heading}>
        <div className="consent-dialog-heading"><h2 id={heading}>{copy.title}</h2><button type="button" onClick={onClose}>{copy.close}</button></div>
        <p>{copy.intro}</p>
        <div className="consent-categories">{config.categories.map(category => <label key={category}>
          <input type="checkbox" checked={category === "NECESSARY" || Boolean(draft[category])} disabled={category === "NECESSARY"} onChange={event => setDraft(current => ({ ...current, [category]: event.target.checked }))} />
          <span><strong>{copy.categories[category][0]}</strong><span>{copy.categories[category][1]}</span></span>
        </label>)}</div>
        <button type="button" className="consent-save" onClick={onSave}>{copy.save}</button>
        {actions}<PolicyLinks copy={copy} />{error && <p role="alert">{copy.error}</p>}
      </section>
    </div>}
    {!open && known && <button type="button" className="consent-revisit" onClick={onOpen}>{copy.revisit}</button>}
  </div>;
}
